import { createContext, useContext } from 'react'

import type { Mobility, TransportService } from '../../api/transport'

/**
 * One booking's answers, held in memory only (owner, M39-Q3): a refresh
 * starts again from Screen 1, and nothing is written to session or local
 * storage. The safety check's answers are never kept, only when it was
 * passed (spec §C.3.1, "Not stored").
 */
export type Booking = {
  gatePassedAt: string | null
  mobility: Mobility | null
  usesOxygen: boolean | null
  /** Screen 2's choice (M39-T10). */
  service: TransportService | null
  /** Screen 3's answers (M39-T11); cleared when the service changes. */
  trip: Trip | null
  /** Screen 4's answers (M39-T12). No medical information, no email. */
  details: Details | null
  /** Screen 4 as typed, kept when the person leaves it unfinished (Back,
   * or Edit mobility), so it comes back as they left it. */
  detailsDraft: DetailsDraft | null
  /** "Book the same trip again" (M40-T11): the earlier trip's answers, to
   * fill Screens 2 and 3. Never a date or a time: the customer chooses. */
  carried: CarriedTrip | null
}

/**
 * The trip carried into a new booking (M40-T11). In memory only, like the
 * booking itself (M39-Q3). From the booking page, which never has the
 * address (§E.8), the area and hospital come as names and are matched to the
 * options; from Screen 6 everything is still in memory.
 */
export type CarriedTrip = {
  service: TransportService
  pickupAreaId: string | null
  pickupAreaName: string | null
  pickupAddress: string | null
  pickupLandmark: string | null
  hospitalId: string | null
  hospitalName: string | null
  journey: 'one_way' | 'return' | null
  returnMode: 'fixed' | 'call_to_collect' | null
  hireLength: 'half' | 'full' | null
}

/** Where and when (spec §C.3.3). Times are ISO 8601 with Accra's offset. */
export type Trip = {
  pickupAreaId: string
  pickupAddress: string
  pickupLandmark: string | null
  pickupLat: number | null
  pickupLng: number | null
  hospitalId: string
  /** Not for Car Hire, which has a start time and no appointment. */
  appointmentAt: string | null
  pickupAt: string
  journey: 'one_way' | 'return' | null
  returnMode: 'fixed' | 'call_to_collect' | null
  returnPickupAt: string | null
  hireLength: 'half' | 'full' | null
  hireStops: string | null
}

/** Who books, who travels, and what they need (spec §C.3.4), in the shape
 * the checkout takes (M39-T04a's `CheckoutRequest`). */
export type Details = {
  bookerFirstName: string
  bookerLastName: string
  /** International form, +233 and 9 digits. */
  bookerPhone: string
  passengerIsBooker: boolean
  /** Only when someone else travels. */
  passengerName: string | null
  passengerAge: number | null
  passengerPhone: string | null
  consentGiven: boolean
  ownWheelchair: boolean
  /** The trip's `wheelchair_needed`: one supplied, priced in the quote. */
  wheelchairNeeded: boolean
  ownOxygen: boolean
  /** The trip's `companion`; a nurse escort is by phone only. */
  companion: 'none' | 'caregiver'
  driverNotes: string | null
}

/** Screen 4's form, unchecked: what was typed and ticked. */
export type DetailsDraft = {
  first: string
  last: string
  phone: string
  someoneElse: boolean
  passengerName: string
  age: string
  passengerPhone: string
  consent: boolean
  wheelchair: 'none' | 'own' | 'needed'
  ownOxygen: boolean
  companion: 'none' | 'caregiver'
  notes: string
}

export const EMPTY_BOOKING: Booking = {
  gatePassedAt: null,
  mobility: null,
  usesOxygen: null,
  service: null,
  trip: null,
  details: null,
  detailsDraft: null,
  carried: null,
}

export type BookingContextValue = {
  booking: Booking
  update: (patch: Partial<Booking>) => void
}

export const BookingContext = createContext<BookingContextValue | null>(null)

export function useBooking(): BookingContextValue {
  const ctx = useContext(BookingContext)
  if (!ctx) throw new Error('useBooking needs a BookingProvider')
  return ctx
}
